<template>
  <!-- crossorigin is needed to set request.mode="cors" , without which
    the serviceworker will see an opaque response which is dangerous to
    cache. See: #7397

    Any changes to this attribute should be kept in sync with the article
    card, and also the preload code in `_loadImagesForDocs` in the
    model.
  -->
  <div class="book-cover">
    <component
      :is="lazyComponent"
      v-if="doc.cover_image && !useFallbackCover"
      :src="coverUrl"
      crossorigin="anonymous"
      class="book-cover__image"
      :class="coverClass"
      :sizes="sizes"
      :srcset="srcset"
      alt=""
    />
    <BookFallbackCover
      v-if="IS_PRERENDERING || !doc.cover_image || useFallbackCover"
      :title="doc.title"
      :subtitle="doc.author"
      :class="fallbackCoverClass"
    />
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'

import type { DocCoverProps } from '@/models/interfaces'
import { cacheBust as addCacheBust } from '@/models/doc'

import BookFallbackCover from './BookFallbackCover.vue'
import { IS_PRERENDERING } from '@/init/settings'
import LazyImage from '@/app/components/podcast/card/LazyImage.vue'
import PublicImage from '@/app/components/podcast/card/PublicImage.vue'
import { isContentTranslated } from '@/services/weglot'

const props = defineProps<{
  doc: DocCoverProps
  coverClass?: string
  fallbackCoverClass?: string
  cacheBust?: boolean
  sizes?: string
  srcset?: string
}>()

const useFallbackCover = computed(() => {
  return isContentTranslated()
})

const coverUrl = computed(() => {
  if (!props.doc.cover_image) {
    return
  }

  if (props.cacheBust) {
    return addCacheBust(props.doc.cover_image)
  }

  return props.doc.cover_image
})

const lazyComponent = computed(() => {
  return !IS_PRERENDERING ? LazyImage : PublicImage
})
</script>

<style lang="scss">
.book-cover {
  flex-shrink: 0;

  // Hide the fallback cover when the original cover already exists,
  // and the page isn't translated. This is only used for public pages:
  // in the app we never render both covers at the same time.
  &__image + .fallback-cover:lang(en) {
    display: none !important;
  }

  // Hide the original cover on translated public pages.
  &__image:not(:lang(en)) {
    display: none !important;
  }
}
</style>
